import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon, ExclamationTriangleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { paymentApi } from '../../api/payment.api';
import type { Payment, ValidateAmountResponse, AutoDetectResponse } from '../../types';

interface SmartAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: Payment;
  onSuccess: () => void;
}

export const SmartAllocationModal: React.FC<SmartAllocationModalProps> = ({
  isOpen,
  onClose,
  payment,
  onSuccess
}) => {
  const [affaireIds, setAffaireIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [validation, setValidation] = useState<ValidateAmountResponse | null>(null);
  const [detectionResult, setDetectionResult] = useState<AutoDetectResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setInputValue('');
      setValidation(null);
      setDetectionResult(null);
      setError(null);
      
      // Check if payment already has affaires allocated
      if (payment.affaires && payment.affaires.length > 0) {
        // Load existing affaire IDs
        const existingIds = payment.affaires.map(a => a.affaire_id);
        setAffaireIds(existingIds);
        // Validate the existing affaires
        handleValidate(existingIds);
      } else {
        // No existing affaires, try auto-detect
        setAffaireIds([]);
        handleAutoDetect();
      }
    }
  }, [isOpen, payment.id]);

  const handleAutoDetect = async () => {
    setIsDetecting(true);
    setError(null);
    
    try {
      const result = await paymentApi.autoDetectAffaires(payment.id);
      setDetectionResult(result);
      
      if (result.data.detected && result.data.affaire_ids) {
        setAffaireIds(result.data.affaire_ids);
        // Auto-validate if affaires detected
        await handleValidate(result.data.affaire_ids);
      }
    } catch (err) {
      console.error('Auto-detection failed:', err);
      setError('Erreur lors de la détection automatique');
    } finally {
      setIsDetecting(false);
    }
  };

  const handleAddAffaire = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !affaireIds.includes(trimmed)) {
      const newIds = [...affaireIds, trimmed];
      setAffaireIds(newIds);
      setInputValue('');
      handleValidate(newIds);
    }
  };

  const handleRemoveAffaire = (id: string) => {
    const newIds = affaireIds.filter(a => a !== id);
    setAffaireIds(newIds);
    if (newIds.length > 0) {
      handleValidate(newIds);
    } else {
      setValidation(null);
    }
  };

  const handleValidate = async (ids: string[] = affaireIds) => {
    if (ids.length === 0) return;
    
    setIsValidating(true);
    setError(null);
    
    try {
      const result = await paymentApi.validatePaymentAmount(payment.id, ids);
      setValidation(result);
    } catch (err) {
      console.error('Validation failed:', err);
      setError('Erreur lors de la validation');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSmartSplit = async () => {
    if (affaireIds.length === 0) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await paymentApi.smartSplitPayment(payment.id, affaireIds);
      
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.message || 'Erreur lors de la répartition');
      }
    } catch (err) {
      console.error('Smart split failed:', err);
      setError('Erreur lors de la répartition intelligente');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: payment.currency || 'EUR'
    }).format(amount);
  };

  const getValidationColor = () => {
    if (!validation) return 'gray';
    switch (validation.data.status) {
      case 'EXACT_MATCH': return 'green';
      case 'OVERPAYMENT': return 'amber';
      case 'UNDERPAYMENT': return 'orange';
      default: return 'red';
    }
  };

  const getValidationIcon = () => {
    if (!validation) return null;
    const color = getValidationColor();
    
    if (validation.data.status === 'EXACT_MATCH') {
      return <CheckIcon className={`h-5 w-5 text-${color}-600`} />;
    } else {
      return <ExclamationTriangleIcon className={`h-5 w-5 text-${color}-600`} />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <SparklesIcon className="h-6 w-6 text-blue-600" />
              Répartition Intelligente
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Paiement #{payment.id} - {formatAmount(payment.amount)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Auto-detection Result or Existing Affaires Info */}
          {(detectionResult || (payment.affaires && payment.affaires.length > 0)) && (
            <div className={`mb-6 p-4 rounded-lg ${
              (detectionResult?.data.detected || (payment.affaires && payment.affaires.length > 0))
                ? 'bg-blue-50 border border-blue-200' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-start gap-3">
                <SparklesIcon className={`h-5 w-5 mt-0.5 ${
                  (detectionResult?.data.detected || (payment.affaires && payment.affaires.length > 0)) ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  {payment.affaires && payment.affaires.length > 0 && !detectionResult ? (
                    <>
                      <p className="font-medium text-gray-900">
                        {payment.affaires.length} affaire(s) déjà allouée(s)
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Montant total alloué: {formatAmount(payment.total_allocated || 0)}
                      </p>
                      {payment.is_fully_allocated && (
                        <p className="text-sm text-green-600 font-medium mt-1">
                          ✓ Entièrement alloué
                        </p>
                      )}
                    </>
                  ) : detectionResult?.data.detected ? (
                    <>
                      <p className="font-medium text-gray-900">
                        {detectionResult.data.affaire_ids?.length} affaire(s) détectée(s)
                      </p>
                      {detectionResult.data.confidence && (
                        <p className="text-sm text-gray-600 mt-1">
                          Confiance: {detectionResult.data.confidence}
                        </p>
                      )}
                      {detectionResult.data.auto_allocate_recommended && (
                        <p className="text-sm text-blue-600 font-medium mt-1">
                          ✓ Allocation automatique recommandée
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-600">
                      {detectionResult?.data.message || 'Aucune affaire détectée dans la communication'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Affaire Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numéros d'affaire
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddAffaire()}
                placeholder="Entrez un numéro d'affaire"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddAffaire}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ajouter
              </button>
              <button
                onClick={handleAutoDetect}
                disabled={isDetecting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <SparklesIcon className="h-4 w-4" />
                {isDetecting ? 'Détection...' : 'Auto-détecter'}
              </button>
            </div>

            {/* Selected Affaires */}
            {affaireIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {affaireIds.map((id) => (
                  <div
                    key={id}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full"
                  >
                    <span className="font-medium">{id}</span>
                    <button
                      onClick={() => handleRemoveAffaire(id)}
                      className="ml-1 hover:text-blue-900"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Validation Result */}
          {validation && (
            <div className={`mb-6 p-4 rounded-lg border ${
              validation.data.status === 'EXACT_MATCH' 
                ? 'bg-green-50 border-green-200'
                : validation.data.status === 'OVERPAYMENT'
                ? 'bg-amber-50 border-amber-200'
                : validation.data.status === 'UNDERPAYMENT'
                ? 'bg-orange-50 border-orange-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {getValidationIcon()}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {validation.data.recommendation}
                  </p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p>Montant du paiement: {formatAmount(validation.data.payment_amount)}</p>
                    <p>Total attendu: {formatAmount(validation.data.expected_total)}</p>
                    {validation.data.difference !== 0 && (
                      <p className="font-medium">
                        Différence: {formatAmount(Math.abs(validation.data.difference))} 
                        {validation.data.difference_percentage && (
                          <span className="text-gray-600">
                            {' '}({Math.abs(validation.data.difference_percentage).toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Affaire Details */}
              {validation.data.affaire_details && validation.data.affaire_details.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Détails des affaires:</p>
                  <div className="space-y-2">
                    {validation.data.affaire_details.map((affaire) => (
                      <div key={affaire.affaire_id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{affaire.affaire_id}</span>
                          <span className="text-gray-600 ml-2">{affaire.demandeur}</span>
                        </div>
                        <span className="font-medium">{formatAmount(affaire.expected_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <div className="flex gap-2">
            {affaireIds.length > 0 && !validation && (
              <button
                onClick={() => handleValidate()}
                disabled={isValidating}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                {isValidating ? 'Validation...' : 'Valider les montants'}
              </button>
            )}
            {validation && (
              <button
                onClick={handleSmartSplit}
                disabled={isProcessing || affaireIds.length === 0}
                className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  validation.data.status === 'EXACT_MATCH'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <SparklesIcon className="h-4 w-4" />
                {isProcessing ? 'Traitement...' : 'Répartir intelligemment'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};